import { create } from 'zustand'
// Data will be fetched from API; no local default import

// Keep in sync with the structure used in Map.tsx
export interface Store {
  description: string
  municipality: string
  location: string
  lat: number
  lng: number
  people: number
  year: number
  amount: number
  type: String
  trees: String
  size: number
}

interface AppState {
  // global stores data and derived counts
  storesData: Store[]
  countForest: number
  countSeed: number
  totalSize: number
  seedGather: number
  treesPlanted: number
  participants: number
  startYear: number
  loading: boolean
  error: string | null
  setStoresData: (stores: Store[]) => void
  loadStores: (walletAddress: string) => Promise<void>
}

export const useAppStore = create<AppState>((set) => {
  const initialStores: Store[] = []
  const { countForest, countSeed } = calculateCounts(initialStores)
  const totalSize = calculateSize(initialStores)
  const seedGather = calculateSeedGather(initialStores)
  const treesPlanted = calculateTreesPlanted(initialStores)
  const participants = calculateNumberOfParticipants(initialStores)
  const startYear = calculateStartingYear(initialStores);

  return {
    // stores
    storesData: initialStores,
    countForest,
    countSeed,
    totalSize,
    seedGather,
    treesPlanted,
    participants,
    startYear,
    loading: false,
    error: null,
    setStoresData: (stores) =>
      set(() => ({
        storesData: stores,
        ...calculateCounts(stores),
        totalSize: calculateSize(stores),
        seedGather: calculateSeedGather(stores),
        treesPlanted: calculateTreesPlanted(stores),
        participants: calculateNumberOfParticipants(stores),
        startYear: calculateStartingYear(stores),
        loading: false,
        error: null,
      })),
    loadStores: async (walletAddress: string) => {
      set({ loading: true, error: null })
      try {
        const baseServerUrl = import.meta.env.VITE_SERVER_URL || ''
        const url = `${baseServerUrl}/api/forestry-project-data?wallet-address=${encodeURIComponent(walletAddress)}`
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`)
        }
        const data = (await response.json()) as Store[]
        set(() => ({
          storesData: data,
          ...calculateCounts(data),
          totalSize: calculateSize(data),
          seedGather: calculateSeedGather(data),
          treesPlanted: calculateTreesPlanted(data),
          participants: calculateNumberOfParticipants(data),
          startYear: calculateStartingYear(data),
          loading: false,
          error: null,
        }))
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        set({ loading: false, error: message })
      }
    },
  }
})

function calculateCounts(stores: Store[]) {
  const countSeed = stores.filter((s) => s.type === 'sakupljanje semena').length
  const countForest = stores.length - countSeed
  return { countForest, countSeed }
}

function calculateSize(stores: Store[]) {
  let size = 0;
  stores.filter((s) => s.type !== 'sakupljanje semena')
        .map(e => e.size)
        .forEach(e => size += e);
  return size;
}

function calculateSeedGather(stores: Store[]) {
  let seeds = 0;
  stores.filter((s) => s.type === 'sakupljanje semena')
        .map(e => e.amount)
        .forEach(e => seeds += e);
  return seeds;
}

function calculateTreesPlanted(stores: Store[]) {
  let trees = 0;
  stores.filter((s) => s.type !== 'sakupljanje semena')
        .map(e => e.amount)
        .forEach(e => trees += e);
  return trees;
}

function calculateNumberOfParticipants(stores: Store[]) {
  let participants = 0;
  stores.forEach(e => participants += e.people);
  return participants;
}

function calculateStartingYear(stores: Store[]) {
  if(stores.length == 0) {
    return new Date().getFullYear();
  }
  let startYear = stores[0].year;
  for (let i = 1; i < stores.length; i++){
    if(stores[i].year < startYear) {
      startYear = stores[i].year;
    }
  }
  return startYear;
}